import { useEffect, useMemo, useState } from 'react';
import type { HandlersBulkSimPauseResumeResponse } from 'types/__generated__/SimInventoryApi';
import type { HologramSimIdsMap, SimInventoryBulkStateAction } from 'types/common';
import useBulkStateChangeCall from './useBulkStateChangeCall';

export type BulkStateChangeError = string;

interface UseBulkStateChangeReturnVal {
  bulkStateResponse: Partial<HandlersBulkSimPauseResumeResponse>;
  isLoading: boolean;
  error?: BulkStateChangeError;
}

interface UseBulkStateChangeProps {
  organizationId?: OrgId;
  action: SimInventoryBulkStateAction;
  sims?: HologramSimIdsMap;
}

// TODO: In strict mode, this makes two preview calls. Investigate why

const useBulkStateChangePreview = ({
  organizationId,
  action,
  sims,
}: UseBulkStateChangeProps): UseBulkStateChangeReturnVal => {
  const [data, setData] = useState<HandlersBulkSimPauseResumeResponse>();
  const [error, setError] = useState<BulkStateChangeError>();
  const [isLoading, setIsLoading] = useState(false);

  const bulkActionPreview = useBulkStateChangeCall({ organizationId, action, preview: true });

  useEffect(() => {
    const getBulkActionResponse = async (simsToPreview: Array<HologramSimId>) => {
      let result: HandlersBulkSimPauseResumeResponse | undefined;
      try {
        result = await bulkActionPreview(simsToPreview);

        setData(result);
        setError(undefined);
      } catch (ex) {
        if (typeof ex === 'string') {
          setError(ex);
          setData(undefined);
        } else {
          setError(`Unknown error occurred: ${ex}`);
          setData(undefined);
          // eslint-disable-next-line no-console
          console.error(ex);
        }
      }

      setIsLoading(false);
    };

    if (!data && !isLoading && !error) {
      if (sims && sims.size && sims.size > 0 && organizationId) {
        setIsLoading(true);
        setError(undefined);
        getBulkActionResponse([...sims.keys()]);
      } else {
        setError('Please select SIMs to take actions...');
      }
    }
  }, [bulkActionPreview, data, error, isLoading, organizationId, sims]);

  const bulkStateResponse = useMemo(() => data ?? {}, [data]);

  return {
    bulkStateResponse,
    error,
    isLoading,
  };
};

export default useBulkStateChangePreview;
